import { useQuery } from '@tanstack/react-query';
import {
  userActivityApi,
  resolveTimeZone,
  type ActivityInterval,
  type ActivityScope,
} from '@/lib/api/user-activity';
import { useAuthStore } from '@/stores/auth-store';

export const userActivityKeys = {
  all: ['user-activity'] as const,
  list: (
    interval: ActivityInterval,
    page: number,
    limit: number,
    timeZone: string,
    scope?: ActivityScope,
    scopeId?: string
  ) =>
    [...userActivityKeys.all, interval, page, limit, timeZone, scope, scopeId] as const,
};

export function useUserActivity(
  interval: ActivityInterval = 'week',
  page: number = 1,
  limit: number = 30,
  scope?: ActivityScope,
  scopeId?: string
) {
  const userTimeZone = useAuthStore((s) => s.user?.timeZone);
  const timeZone = resolveTimeZone(userTimeZone);

  // When team scope has no specific team selected ("All Teams"), fall back to
  // scope=all so the query still fires (matching Epics/Requests page behavior).
  const effectiveScope: ActivityScope | undefined =
    scope === 'team' && !scopeId ? 'all' : scope;
  const effectiveScopeId: string | undefined =
    scope === 'team' && !scopeId ? undefined : scopeId;

  return useQuery({
    queryKey: userActivityKeys.list(interval, page, limit, timeZone, effectiveScope, effectiveScopeId),
    queryFn: () => {
      const params: { interval: ActivityInterval; page: number; limit: number; timeZone: string; scope?: ActivityScope; scopeId?: string } = {
        interval,
        page,
        limit,
        timeZone,
      };
      if (effectiveScope !== undefined) params.scope = effectiveScope;
      if (effectiveScopeId !== undefined) params.scopeId = effectiveScopeId;
      return userActivityApi.get(params);
    },
    enabled: !(scope === 'user' && !scopeId),
    // Dashboard activity data changes slowly and is invalidated on mutation
    // (see invalidateUserActivityCacheForUser on the server), so aggressive
    // 30s polling + refetch-on-focus was pure overhead. Poll every 5 minutes
    // as a safety net and let mutation-driven invalidation handle freshness.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
